import {
    convertYesNoToBoolean,
    getApexAccountTypeLabelValueOptions,
    getInvestmentManagementAccountTypeLabelValueOptions,
    transformEnumToLabelValueOption,
} from '@fruitful/fruitils';
import { MoneyMapCategoryToAccountColorMap } from '@fruitful/money-map';
import {
    AccountOwnership,
    HolisticCategory,
    InvestmentManagementAccountType,
    type MoneyMapAccountCreateParams,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    type MoneyMapBankAccountDetails,
    type MoneyMapCategory,
    type MoneyMapIMAccountDetails,
    MoneyMapRuleType,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
    Portfolio,
    ReadableApexAccountRegistrationType,
    RiskLevel,
    SupportedApexAccountRegistrationType,
    TimeHorizon,
    YesNo,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronUpIcon,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    Icon,
    PICKER_PLACEHOLDER,
    Row,
    RowListHasHeaderContext,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { isBackdoorTradEligibleMoneyMapAccount } from '../../utils/goalFormBackdoorRothUtils';
import {
    getMoneyMapAccountDropdownLabel,
    getMoneyMapAccountOwnership,
} from '../../utils/moneyMapUtils';
import { ControlledInputRow, ControlledPickerRow } from './ControlledFormRows';

export enum AccountSettingsFormType {
    NONE = 'NONE',
    NEW_CASH_ACCOUNT = 'NEW_CASH_ACCOUNT',
    NEW_IM_ACCOUNT = 'NEW_IM_ACCOUNT',
    NEW_APEX_IM_ACCOUNT = 'NEW_APEX_IM_ACCOUNT',
    EXISTING_DEPOSITORY_ACCOUNT = 'EXISTING_DEPOSITORY_ACCOUNT',
    EXISTING_APEX_ACCOUNT = 'EXISTING_APEX_ACCOUNT',
    LINKED_ACCOUNT = 'LINKED_ACCOUNT',
}

const baseAccountSettingsFormSchema = z.object({
    // Existing account id, or reference to new cash/IM account option
    moneyMapAccountId: z
        .string()
        .min(1, { message: 'Account is required' })
        .regex(new RegExp(`^(?!${PICKER_PLACEHOLDER}$).+`), {
            message: `"${PICKER_PLACEHOLDER}" is not a valid option`,
        }),
    // Optional create new parameters
    name: z.string().optional(),
    ownership: z.string().optional(),
    primaryUserId: z.string().optional(),
    category: z.string().optional(),
    // for cash accounts
    virtualCard: z.string().optional(),
    physicalCard: z.string().optional(),
    // for im accounts
    investmentAccountType: z.string().optional(),
    apexInvestmentAccountType: z.string().optional(),
    portfolio: z.string().optional(),
    apexPortfolio: z.string().nullish(),
    riskLevel: z.string().optional(),
    apexRiskLevel: z.string().nullish(),
    apexTimeHorizon: z.string().nullish(),
});

export const accountSettingsFormSchema = z.object({
    accountSettings: baseAccountSettingsFormSchema,
});

export const transferAccountSettingsFormSchema = z.object({
    transferAccountSettings: baseAccountSettingsFormSchema.optional(), // Make optional since when this schema is used to construct parent form schemas, like goalFormSchema, it isn't always required.
});

export type BaseAccountSettingsFormSchema = z.infer<
    typeof baseAccountSettingsFormSchema
>;

export type AccountSettingsFormSchema = z.infer<
    typeof accountSettingsFormSchema
>;

export type TransferAccountSettingsFormSchema = z.infer<
    typeof transferAccountSettingsFormSchema
>;

export type GenericAccountSettingsFormSchema =
    | AccountSettingsFormSchema
    | TransferAccountSettingsFormSchema;

export type AccountFormType = 'account' | 'transferAccount';

/** How to build Apex `createParameters` for backdoor Roth (Trad vs Roth) vs a standard Apex goal account. */
export type ApexCreateParametersVariant =
    | 'standard'
    | 'backdoorTraditional'
    | 'backdoorRoth';

export const getBackdoorTradApexAccountTypeLabelValueOptions = () =>
    [
        SupportedApexAccountRegistrationType.RegistrationIraTraditional,
        SupportedApexAccountRegistrationType.RegistrationIraRollover,
    ].map((value) => ({
        label: ReadableApexAccountRegistrationType[value],
        value,
    }));

// Helper function to extract create parameters from form schema (meant to be used in parent form's onSubmit function)
export const extractCreateParametersFromAccountSettingsToSubmit = (
    schema: GenericAccountSettingsFormSchema,
    category: MoneyMapCategory,
    type:
        | MoneyMapAccountType.INTERNAL_BANKING
        | MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
        | MoneyMapAccountType.INVESTMENT_MANAGEMENT,
    apexVariant: ApexCreateParametersVariant = 'standard',
): MoneyMapAccountCreateParams => {
    const details =
        (schema as AccountSettingsFormSchema).accountSettings ??
        (schema as TransferAccountSettingsFormSchema).transferAccountSettings;

    if (type === MoneyMapAccountType.INTERNAL_BANKING) {
        return {
            name: details.name as string,
            ownership: details.ownership as AccountOwnership,
            primaryUserId: details.primaryUserId,
            category: details.category as HolisticCategory,
            virtualCard: convertYesNoToBoolean(
                details.virtualCard as YesNo,
            ) as boolean,
            physicalCard: convertYesNoToBoolean(
                details.physicalCard as YesNo,
            ) as boolean,
            icon: FRUITFUL_ICON_KEY,
            color: MoneyMapCategoryToAccountColorMap[category] as string,
        };
    }

    if (type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        const base = {
            name: details.name as string,
            ownership: details.ownership as AccountOwnership,
            primaryUserId: details.primaryUserId,
            apexAccountType:
                details.apexInvestmentAccountType as SupportedApexAccountRegistrationType,
            icon: FRUITFUL_ICON_KEY,
            color: MoneyMapCategoryToAccountColorMap[category] as string,
        };
        if (apexVariant === 'backdoorTraditional') {
            return {
                ...base,
                backdoorRothRole: 'TRADITIONAL' as const,
            };
        }
        if (apexVariant === 'backdoorRoth') {
            return {
                ...base,
                apexPortfolio: details.apexPortfolio as Portfolio,
                apexRiskLevel: details.apexRiskLevel as RiskLevel,
                apexTimeHorizon: details.apexTimeHorizon as TimeHorizon,
                backdoorRothRole: 'ROTH' as const,
            };
        }
        return {
            ...base,
            apexPortfolio: details.apexPortfolio as Portfolio,
            apexRiskLevel: details.apexRiskLevel as RiskLevel,
            apexTimeHorizon: details.apexTimeHorizon as TimeHorizon,
        };
    }

    // Orion IM
    return {
        name: details.name as string,
        ownership: details.ownership as AccountOwnership,
        primaryUserId: details.primaryUserId,
        category: details.category as HolisticCategory,
        accountType:
            details.investmentAccountType as InvestmentManagementAccountType,
        portfolio: details.portfolio as Portfolio,
        riskLevel: details.riskLevel as RiskLevel,
    };
};

export type TransferAccountPickerFilter = 'default' | 'backdoorTradEligible';

const getAccountOptions = (
    type: AccountFormType,
    accounts: MoneyMapAccountWithAccountDetails[],
    ruleType?: MoneyMapRuleType,
    transferAccountPickerFilter: TransferAccountPickerFilter = 'default',
    disabledAccountIds?: ReadonlySet<string>,
): Array<{ value: string; label: string; enabled?: boolean }> => {
    if (
        type === 'transferAccount' &&
        transferAccountPickerFilter === 'backdoorTradEligible' &&
        (ruleType === MoneyMapRuleType.GOAL ||
            ruleType === MoneyMapRuleType.ACCOUNT_ONLY)
    ) {
        const filteredAccounts = accounts.filter(
            isBackdoorTradEligibleMoneyMapAccount,
        );
        const accountOptions = orderBy(
            filteredAccounts.map((account) => ({
                label: getMoneyMapAccountDropdownLabel(account),
                value: account.id,
            })),
            'label',
            'asc',
        );
        const newAccountOptions = [NEW_APEX_IM_ACCOUNT_OPTION];
        return [...newAccountOptions, ...accountOptions];
    }

    const filteredAccounts = accounts.filter((account) => {
        // ENG-5965 TEMP: `!IS_PRODUCTION` gates cash (internal banking + new cash option below) in Other accounts
        // until we address unexpected knock-on effects with member money maps; remove when safe.
        if (ruleType === MoneyMapRuleType.ACCOUNT_ONLY) {
            return (
                (!IS_PRODUCTION &&
                    account.type === MoneyMapAccountType.INTERNAL_BANKING) ||
                account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
            );
        }

        // Exclude IM accounts for all non-Goal rule types.
        if (ruleType !== MoneyMapRuleType.GOAL) {
            if (
                account.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT ||
                account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
            ) {
                return false;
            }
        }

        // Exclude external banking accounts on the income form (which is inferred from a null ruleType)
        if (ruleType == null) {
            if (account.type === MoneyMapAccountType.EXTERNAL_BANKING) {
                return false;
            }
        }

        if (type === 'transferAccount') {
            // Exclude quiltt accounts and IM accounts if setting up a transfer account (i.e. an account to hold funds that is transferable)
            return (
                account.quilttAccountId == null &&
                account.type !== MoneyMapAccountType.INVESTMENT_MANAGEMENT &&
                account.type !== MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
            );
        }

        return true;
    });

    const markDisabled = (
        opts: Array<{ value: string; label: string }>,
    ): Array<{ value: string; label: string; enabled?: boolean }> =>
        disabledAccountIds == null || disabledAccountIds.size === 0
            ? opts
            : opts.map((o) =>
                  disabledAccountIds.has(o.value)
                      ? { ...o, enabled: false }
                      : o,
              );

    let accountOptions: Array<{ value: string; label: string }>;

    if (type === 'account') {
        // Order external accounts below internal, and group external accounts by category
        const externalAccountOptions = filteredAccounts
            .filter(
                (account) =>
                    account.type === MoneyMapAccountType.EXTERNAL_HOLISTIC ||
                    account.type === MoneyMapAccountType.EXTERNAL_BANKING,
            )
            .map((account) => ({
                label: getMoneyMapAccountDropdownLabel(account),
                value: account.id,
            }));

        const otherAccountOptions = filteredAccounts
            .filter(
                (account) =>
                    account.type !== MoneyMapAccountType.EXTERNAL_HOLISTIC &&
                    account.type !== MoneyMapAccountType.EXTERNAL_BANKING,
            )
            .map((account) => ({
                label: getMoneyMapAccountDropdownLabel(account),
                value: account.id,
            }));

        const orderedExternalAccountOptions = orderBy(
            externalAccountOptions,
            'label',
            'desc',
        );

        accountOptions = [
            ...otherAccountOptions,
            ...orderedExternalAccountOptions,
        ];
    } else {
        accountOptions = filteredAccounts.map((account) => ({
            label: getMoneyMapAccountDropdownLabel(account),
            value: account.id,
        }));
    }

    const imAccountOptions = [NEW_APEX_IM_ACCOUNT_OPTION];

    if (ruleType === MoneyMapRuleType.ACCOUNT_ONLY) {
        return markDisabled([
            ...(type === 'account'
                ? [
                      // ENG-5965 TEMP: !IS_PRODUCTION gate as ACCOUNT_ONLY filter above.
                      ...(!IS_PRODUCTION ? [NEW_CASH_ACCOUNT_OPTION] : []),
                      ...imAccountOptions,
                  ]
                : []),
            ...accountOptions,
        ]);
    }

    return markDisabled([
        NEW_CASH_ACCOUNT_OPTION,
        ...(type === 'account' && ruleType === MoneyMapRuleType.GOAL
            ? imAccountOptions
            : []), // Only include IM account on account settings, and not on transfer settings; and only for Goal rule types.
        ...accountOptions,
    ]);
};

interface AccountSettingsFormModuleProps<TContext> {
    type: 'account' | 'transferAccount';
    accounts: MoneyMapAccountWithAccountDetails[];
    selectedAccount?: MoneyMapAccountWithAccountDetails;
    formFunctions: UseFormReturn<GenericAccountSettingsFormSchema, TContext>;
    isUsingPreset?: boolean;
    ruleType?: MoneyMapRuleType;
    /** When set on the transfer-account panel, only Trad/Rollover Apex accounts (or new Apex) are listed. */
    transferAccountPickerFilter?: TransferAccountPickerFilter;
    /** Full Apex IM pickers, or backdoor Trad: show portfolio/risk/time as read-only N/A (not applicable for pass-through Trad). */
    apexImFieldsMode?: 'full' | 'backdoorTradNa';
    /** Account type options for new Apex on the transfer panel (backdoor Trad: Traditional/Rollover only). */
    apexAccountTypeOptionsOverride?: Array<{ label: string; value: string }>;
    /** When set (e.g. backdoor Trad), restrict new IM account type picker — default is all IM types. */
    imAccountTypeOptionsOverride?: Array<{ label: string; value: string }>;
    /** Overrides the section title when `type` is `transferAccount` (e.g. backdoor Trad vs income split). */
    transferFormModuleTitle?: string;
    /** When true, the account picker dropdown is disabled (e.g. existing backdoor Trad pair). */
    disableAccountPicker?: boolean;
    /** Account IDs to show as disabled (non-selectable) in the account picker. */
    disabledAccountIds?: ReadonlySet<string>;
    /** Optional helper text rendered as a footer inside the account card (e.g. shared-account notice). */
    bottomNotice?: string | null;
}

export const AccountSettingsFormModule = <TContext,>({
    type,
    accounts,
    selectedAccount,
    formFunctions,
    isUsingPreset = false,
    ruleType,
    transferAccountPickerFilter = 'default',
    apexImFieldsMode = 'full',
    apexAccountTypeOptionsOverride,
    imAccountTypeOptionsOverride,
    transferFormModuleTitle,
    disableAccountPicker = false,
    disabledAccountIds,
    bottomNotice,
}: AccountSettingsFormModuleProps<TContext>) => {
    const {
        control,
        register,
        watch,
        setValue,
        formState: { errors },
    } = formFunctions;
    const [isExpanded, setIsExpanded] = useState(true);

    const selectedMembership = useAtomValue(selectedMembershipAtom);
    const { users } = selectedMembership ?? {};
    const isSoloMembership = users?.length === 1;

    const accountOptions = getAccountOptions(
        type,
        accounts,
        ruleType,
        transferAccountPickerFilter,
        disabledAccountIds,
    );

    const moneyMapAccountId = watch(`${type}Settings.moneyMapAccountId`);
    const accountOwnership = watch(`${type}Settings.ownership`);
    const accountCategory = watch(`${type}Settings.category`);
    const virtualCard = watch(`${type}Settings.virtualCard`);
    const apexAccountType = watch(`${type}Settings.apexInvestmentAccountType`);
    const accountName = watch(`${type}Settings.name`);

    const clearFieldsForNewAccount = () => {
        setValue(`${type}Settings.name`, '');
        setValue(`${type}Settings.ownership`, '');
        setValue(`${type}Settings.primaryUserId`, '');
        setValue(`${type}Settings.category`, '');
        setValue(`${type}Settings.virtualCard`, '');
        setValue(`${type}Settings.physicalCard`, '');
        setValue(`${type}Settings.investmentAccountType`, '');
        setValue(`${type}Settings.portfolio`, '');
        setValue(`${type}Settings.riskLevel`, '');
        setValue(`${type}Settings.apexInvestmentAccountType`, '');
        setValue(`${type}Settings.apexPortfolio`, '');
        setValue(`${type}Settings.apexRiskLevel`, '');
        setValue(`${type}Settings.apexTimeHorizon`, '');
    };

    const setFieldsForSelectedExistingAccount = (
        account: MoneyMapAccountWithAccountDetails,
    ) => {
        // First clear all fields out
        clearFieldsForNewAccount();

        // Fill out disabled form fields for existing accounts
        setValue(`${type}Settings.name`, account.accountDetails?.name);
        setValue(
            `${type}Settings.ownership`,
            getMoneyMapAccountOwnership(account),
        );
        setValue(
            `${type}Settings.primaryUserId`,
            account.accountDetails?.primaryUserId,
        );

        if (account.type === MoneyMapAccountType.INTERNAL_BANKING) {
            const { holisticCategory, hasVirtualCard, hasPhysicalCard } =
                (account.accountDetails ?? {}) as MoneyMapBankAccountDetails;

            setValue(`${type}Settings.category`, holisticCategory);
            setValue(
                `${type}Settings.virtualCard`,
                hasVirtualCard ? YesNo.YES : YesNo.NO,
            );
            setValue(
                `${type}Settings.physicalCard`,
                hasPhysicalCard ? YesNo.YES : YesNo.NO,
            );
        }

        if (account.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT) {
            const { accountType, portfolio, riskLevel } =
                (account.accountDetails ?? {}) as MoneyMapIMAccountDetails;

            setValue(`${type}Settings.investmentAccountType`, accountType);
            setValue(`${type}Settings.portfolio`, portfolio);
            setValue(`${type}Settings.riskLevel`, riskLevel);
        }

        if (account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
            const {
                apexAccountType,
                apexPortfolio,
                apexRiskLevel,
                apexTimeHorizon,
            } = (account.accountDetails ?? {}) as MoneyMapApexAccountDetails;

            setValue(
                `${type}Settings.apexInvestmentAccountType`,
                apexAccountType,
            );
            setValue(`${type}Settings.apexPortfolio`, apexPortfolio);
            setValue(`${type}Settings.apexRiskLevel`, apexRiskLevel);
            setValue(`${type}Settings.apexTimeHorizon`, apexTimeHorizon);
        }
    };

    // Set account ownership to individual for solo memberships
    const setIndividualAccountOwnership = () => {
        if (isSoloMembership) {
            setValue(`${type}Settings.ownership`, AccountOwnership.INDIVIDUAL);
            setValue(
                `${type}Settings.primaryUserId`,
                (users ?? [])[0]?.id ?? '',
            );
        }
    };

    // Derive the form type synchronously so the section renders the right
    // fields on the very first paint (a useEffect-driven state would briefly
    // show only the picker, making the section appear collapsed).
    const formType = useMemo<AccountSettingsFormType>(() => {
        const isNewCashAccount =
            moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;
        const isNewIMAccount =
            moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value;
        const isNewApexAccount =
            moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;

        if (
            isNewCashAccount ||
            (selectedAccount?.type === MoneyMapAccountType.INTERNAL_BANKING &&
                selectedAccount?.status === MoneyMapAccountStatus.PROPOSED)
        ) {
            return AccountSettingsFormType.NEW_CASH_ACCOUNT;
        }
        if (
            isNewIMAccount ||
            (selectedAccount?.type ===
                MoneyMapAccountType.INVESTMENT_MANAGEMENT &&
                selectedAccount?.status === MoneyMapAccountStatus.PROPOSED)
        ) {
            return AccountSettingsFormType.NEW_IM_ACCOUNT;
        }
        if (
            isNewApexAccount ||
            (selectedAccount?.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
                selectedAccount?.status === MoneyMapAccountStatus.PROPOSED)
        ) {
            return AccountSettingsFormType.NEW_APEX_IM_ACCOUNT;
        }
        if (
            selectedAccount?.type === MoneyMapAccountType.INTERNAL_BANKING &&
            selectedAccount?.status === MoneyMapAccountStatus.EXISTS
        ) {
            return AccountSettingsFormType.EXISTING_DEPOSITORY_ACCOUNT;
        }
        if (
            selectedAccount?.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
            selectedAccount?.status === MoneyMapAccountStatus.EXISTS
        ) {
            return AccountSettingsFormType.EXISTING_APEX_ACCOUNT;
        }
        if (selectedAccount?.type === MoneyMapAccountType.EXTERNAL_HOLISTIC) {
            return AccountSettingsFormType.LINKED_ACCOUNT;
        }
        return AccountSettingsFormType.NONE;
    }, [moneyMapAccountId, selectedAccount]);

    useEffect(() => {
        if (selectedAccount != null) {
            // Populate form fields from account data (accountDetails for existing, createParameters-derived for proposed)
            setFieldsForSelectedExistingAccount(selectedAccount);
        } else if (!isUsingPreset) {
            // Only clear out defaults for a new account if not using a preset (where new account fields are pre-populated)
            clearFieldsForNewAccount();
        }

        const isNewCashAccount =
            moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;
        const isNewIMAccount =
            moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value;
        const isNewApexAccount =
            moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;

        if (isNewCashAccount || isNewIMAccount || isNewApexAccount) {
            // Set individual account ownership for solo memberships on newly proposed cash and IM accounts.
            setIndividualAccountOwnership();
        }
    }, [moneyMapAccountId, isUsingPreset, selectedAccount]);

    // Default new cash accounts for Income and Bills forms with account name of Core and a category of Spend, and no physical card.
    useEffect(() => {
        if (isUsingPreset) {
            return;
        }

        const isNewCashAccount =
            moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;
        const isIncomeOrBills =
            ruleType == null || ruleType === MoneyMapRuleType.BILLS;

        if (isNewCashAccount && isIncomeOrBills) {
            setValue(`${type}Settings.name`, 'Core');
            setValue(`${type}Settings.category`, HolisticCategory.SPEND);
            setValue(`${type}Settings.physicalCard`, YesNo.NO, {
                shouldValidate: true,
            });
        }
        if (ruleType === MoneyMapRuleType.SPEND) {
            setValue(`${type}Settings.category`, HolisticCategory.SPEND);
        }
    }, [moneyMapAccountId]);

    useEffect(() => {
        const isNewCashAccount =
            moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;

        if (isNewCashAccount) {
            // Update virtual card to Yes if category is spend, and to No virtual card if category is save.
            // Validate after setting the value.
            if (accountCategory === HolisticCategory.SPEND) {
                setValue(`${type}Settings.virtualCard`, YesNo.YES, {
                    shouldValidate: true,
                });
            } else if (accountCategory === HolisticCategory.SAVE) {
                setValue(`${type}Settings.virtualCard`, YesNo.NO, {
                    shouldValidate: true,
                });
            }
        }
    }, [accountCategory]);

    // If virtual card is no, update physical card setting to also be no.
    useEffect(() => {
        if (virtualCard === YesNo.NO) {
            setValue(`${type}Settings.physicalCard`, YesNo.NO, {
                shouldValidate: true,
            });
        }
    }, [virtualCard]);

    useEffect(() => {
        if (
            transferAccountPickerFilter === 'backdoorTradEligible' &&
            type === 'transferAccount' &&
            moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value
        ) {
            setValue(
                `${type}Settings.apexInvestmentAccountType`,
                SupportedApexAccountRegistrationType.RegistrationIraTraditional,
                { shouldValidate: true },
            );
        }
    }, [transferAccountPickerFilter, type, moneyMapAccountId, setValue]);

    useEffect(() => {
        if (
            transferAccountPickerFilter === 'backdoorTradEligible' &&
            type === 'transferAccount' &&
            moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value
        ) {
            setValue(
                `${type}Settings.investmentAccountType`,
                InvestmentManagementAccountType.IRA,
                { shouldValidate: true },
            );
            setValue(`${type}Settings.category`, HolisticCategory.INVEST, {
                shouldValidate: true,
            });
        }
    }, [transferAccountPickerFilter, type, moneyMapAccountId, setValue]);

    // Automatically set ownership based on apex account type
    useEffect(() => {
        const isNewApexAccount =
            formType === AccountSettingsFormType.NEW_APEX_IM_ACCOUNT;

        if (
            isNewApexAccount &&
            apexAccountType != null &&
            apexAccountType !== ''
        ) {
            const isJointBrokerage =
                (apexAccountType as SupportedApexAccountRegistrationType) ===
                SupportedApexAccountRegistrationType.RegistrationJointWros;

            if (isJointBrokerage) {
                setValue(`${type}Settings.ownership`, AccountOwnership.JOINT, {
                    shouldValidate: true,
                });
                // Clear primary user ID for joint accounts
                setValue(`${type}Settings.primaryUserId`, '');
            } else {
                setValue(
                    `${type}Settings.ownership`,
                    AccountOwnership.INDIVIDUAL,
                    {
                        shouldValidate: true,
                    },
                );
                // Set primary user ID to first user for solo memberships
                if (isSoloMembership) {
                    setValue(
                        `${type}Settings.primaryUserId`,
                        (users ?? [])[0]?.id ?? '',
                    );
                }
            }
        }
    }, [apexAccountType, formType]);

    const isNewCashAccount =
        formType === AccountSettingsFormType.NEW_CASH_ACCOUNT;
    const isNewIMAccount = formType === AccountSettingsFormType.NEW_IM_ACCOUNT;
    const isNewApexAccount =
        formType === AccountSettingsFormType.NEW_APEX_IM_ACCOUNT;
    const isExistingDepositoryAccount =
        formType === AccountSettingsFormType.EXISTING_DEPOSITORY_ACCOUNT;
    const isExistingApexAccount =
        formType === AccountSettingsFormType.EXISTING_APEX_ACCOUNT;

    const isLinkedAccount = formType === AccountSettingsFormType.LINKED_ACCOUNT;
    const hasVirtualCard = virtualCard === YesNo.YES;

    const getCategoryOptions = () => {
        // Only show spend and save categories for new cash accounts
        if (isNewCashAccount) {
            return transformEnumToLabelValueOption({
                [HolisticCategory.SPEND]: HolisticCategory.SPEND,
                [HolisticCategory.SAVE]: HolisticCategory.SAVE,
            });
        }

        // Only show invest category for new IM accounts
        if (isNewIMAccount || isNewApexAccount) {
            return transformEnumToLabelValueOption({
                [HolisticCategory.INVEST]: HolisticCategory.INVEST,
            });
        }

        return transformEnumToLabelValueOption(HolisticCategory);
    };

    const imAccountTypeOptions =
        getInvestmentManagementAccountTypeLabelValueOptions();
    const imAccountTypeOptionsForPicker =
        imAccountTypeOptionsOverride ?? imAccountTypeOptions;
    const apexAccountTypeOptions =
        getApexAccountTypeLabelValueOptions(isSoloMembership);
    const apexAccountTypeOptionsForPicker =
        apexAccountTypeOptionsOverride ?? apexAccountTypeOptions;

    const sectionTitle =
        type === 'transferAccount'
            ? (transferFormModuleTitle ?? 'Account for income split')
            : 'Account';

    const accountSummary = useMemo(() => {
        const fallbackTitle =
            type === 'transferAccount' ? 'Transfer account' : 'Account';
        const titleFromExisting =
            selectedAccount?.accountDetails?.name ?? undefined;
        const title =
            titleFromExisting ??
            (accountName != null && accountName !== ''
                ? accountName
                : fallbackTitle);

        let subtitle = '';
        if (selectedAccount != null) {
            const matchingOption = accountOptions.find(
                (option) => option.value === selectedAccount.id,
            );
            subtitle = matchingOption?.label ?? '';
        } else if (
            moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value ||
            moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value ||
            moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value
        ) {
            subtitle =
                accountOptions.find(
                    (option) => option.value === moneyMapAccountId,
                )?.label ?? '';
        }

        return { title, subtitle };
    }, [accountName, accountOptions, moneyMapAccountId, selectedAccount, type]);

    const renderFields = () => (
        <YStack padding={16}>
            <ControlledPickerRow
                name={`${type}Settings.moneyMapAccountId`}
                label="Account"
                placeholder={PICKER_PLACEHOLDER}
                control={control}
                register={register}
                errors={errors}
                options={accountOptions}
                disabled={disableAccountPicker}
            />
            {(isNewCashAccount ||
                isNewIMAccount ||
                isNewApexAccount ||
                isExistingDepositoryAccount ||
                isExistingApexAccount) && (
                <>
                    <ControlledInputRow
                        name={`${type}Settings.name`}
                        label="Account name"
                        placeholder="Account name"
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={
                            isExistingDepositoryAccount || isExistingApexAccount
                        }
                        textAlign="left"
                    />
                    {(isNewApexAccount || isExistingApexAccount) && (
                        <ControlledPickerRow
                            name={`${type}Settings.apexInvestmentAccountType`}
                            label="Account type"
                            placeholder={PICKER_PLACEHOLDER}
                            control={control}
                            register={register}
                            errors={errors}
                            disabled={isExistingApexAccount}
                            options={apexAccountTypeOptionsForPicker}
                        />
                    )}
                    <ControlledPickerRow
                        name={`${type}Settings.ownership`}
                        label="Account ownership"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={
                            isExistingDepositoryAccount ||
                            isExistingApexAccount ||
                            isNewApexAccount ||
                            isSoloMembership
                        }
                        options={transformEnumToLabelValueOption(
                            AccountOwnership,
                        )}
                    />
                    {accountOwnership !== AccountOwnership.JOINT && (
                        <ControlledPickerRow
                            name={`${type}Settings.primaryUserId`}
                            label="Primary account owner"
                            placeholder={PICKER_PLACEHOLDER}
                            control={control}
                            register={register}
                            errors={errors}
                            disabled={
                                isExistingDepositoryAccount ||
                                isExistingApexAccount ||
                                isSoloMembership
                            }
                            options={(users ?? []).map((user) => ({
                                label: user.firstName ?? '(unknown)',
                                value: user.id,
                            }))}
                        />
                    )}
                </>
            )}
            {isNewCashAccount && (
                <ControlledPickerRow
                    name={`${type}Settings.category`}
                    label="Account category"
                    placeholder={PICKER_PLACEHOLDER}
                    control={control}
                    register={register}
                    errors={errors}
                    disabled={isExistingDepositoryAccount || isLinkedAccount}
                    options={getCategoryOptions()}
                />
            )}
            {(isNewCashAccount || isExistingDepositoryAccount) && (
                <>
                    <ControlledPickerRow
                        name={`${type}Settings.virtualCard`}
                        label="Virtual card"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={isExistingDepositoryAccount}
                        options={transformEnumToLabelValueOption(YesNo)}
                    />
                    <ControlledPickerRow
                        name={`${type}Settings.physicalCard`}
                        label="Physical card"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        disabled={
                            isExistingDepositoryAccount || !hasVirtualCard
                        }
                        options={transformEnumToLabelValueOption(YesNo)}
                    />
                </>
            )}
            {(isNewApexAccount || isExistingApexAccount) &&
                apexImFieldsMode === 'full' && (
                    <>
                        <ControlledPickerRow
                            name={`${type}Settings.apexPortfolio`}
                            label="Portfolio"
                            placeholder={PICKER_PLACEHOLDER}
                            control={control}
                            register={register}
                            errors={errors}
                            disabled={isExistingApexAccount}
                            options={Object.values(Portfolio).map((value) => ({
                                label: value.toString(),
                                value: value.toString(),
                            }))}
                        />
                        <ControlledPickerRow
                            name={`${type}Settings.apexRiskLevel`}
                            label="Risk level"
                            placeholder={PICKER_PLACEHOLDER}
                            control={control}
                            register={register}
                            errors={errors}
                            disabled={isExistingApexAccount}
                            options={transformEnumToLabelValueOption(RiskLevel)}
                        />
                        <ControlledPickerRow
                            name={`${type}Settings.apexTimeHorizon`}
                            label="Time horizon"
                            placeholder={PICKER_PLACEHOLDER}
                            control={control}
                            register={register}
                            errors={errors}
                            disabled={isExistingApexAccount}
                            options={transformEnumToLabelValueOption(
                                TimeHorizon,
                            )}
                        />
                    </>
                )}
            {(isNewApexAccount || isExistingApexAccount) &&
                apexImFieldsMode === 'backdoorTradNa' && (
                    <YStack
                        gap={12}
                        paddingHorizontal={16}
                        paddingVertical={12}
                        backgroundColor="$gray100"
                    >
                        <BodyMediumRegular>Portfolio: N/A</BodyMediumRegular>
                        <BodyMediumRegular>Risk level: N/A</BodyMediumRegular>
                        <BodyMediumRegular>Time horizon: N/A</BodyMediumRegular>
                    </YStack>
                )}
            {isNewIMAccount && (
                <>
                    <ControlledPickerRow
                        name={`${type}Settings.investmentAccountType`}
                        label="Account type"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        options={imAccountTypeOptionsForPicker}
                    />
                    <ControlledPickerRow
                        name={`${type}Settings.portfolio`}
                        label="Portfolio"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        options={transformEnumToLabelValueOption(Portfolio)}
                    />
                    <ControlledPickerRow
                        name={`${type}Settings.riskLevel`}
                        label="Risk level"
                        placeholder={PICKER_PLACEHOLDER}
                        control={control}
                        register={register}
                        errors={errors}
                        options={transformEnumToLabelValueOption(RiskLevel)}
                    />
                </>
            )}
        </YStack>
    );

    return (
        <YStack borderRadius={24} overflow="hidden" backgroundColor="$gray0">
            <XStack
                backgroundColor="$gray200"
                paddingHorizontal={16}
                paddingVertical={16}
                alignItems="center"
                justifyContent="space-between"
            >
                <BodyLargeRegular>{sectionTitle}</BodyLargeRegular>
            </XStack>
            <RowListHasHeaderContext.Provider value>
                <YStack
                    backgroundColor="$gray100"
                    borderBottomLeftRadius={16}
                    borderBottomRightRadius={16}
                    overflow="hidden"
                >
                    <Row
                        title={accountSummary.title}
                        subtitle={
                            accountSummary.subtitle === ''
                                ? undefined
                                : accountSummary.subtitle
                        }
                        leftContentOverride={
                            <FruitfulCustomIcon
                                icon={FRUITFUL_ICON_KEY}
                                color="red"
                                size="xs"
                            />
                        }
                        rightContent={
                            <Icon
                                icon={
                                    isExpanded ? ChevronUpIcon : ChevronDownIcon
                                }
                                size={16}
                                color="$gray1000"
                            />
                        }
                        onPress={() => {
                            setIsExpanded((v) => !v);
                        }}
                    />
                    {isExpanded && renderFields()}
                    {bottomNotice != null && bottomNotice !== '' && (
                        <XStack backgroundColor="$gray200" padding={16}>
                            <BodySmallRegular color="$gray1000">
                                {bottomNotice}
                            </BodySmallRegular>
                        </XStack>
                    )}
                </YStack>
            </RowListHasHeaderContext.Provider>
        </YStack>
    );
};
